import apiService from './api';

export interface PremiumUsernameDto {
  premiumUsernameId: string;
  userId: string;
  credentialId: string;
  premiumUsername: string;
  originalUsername: string;
  activated: string;
  expiration: string;
  daysUntilExpiration: number;
  status: number;
  renewalFee: number;
  isExpiringSoon: boolean;
}

export interface PremiumUsernameAvailability {
  username: string;
  available: boolean;
  isPremiumFormat: boolean;
  reason?: string;
  fee?: number;
}

export interface PremiumUsernameResult {
  success: boolean;
  error?: string;
  premiumUsername?: PremiumUsernameDto;
}

export interface ClaimPremiumUsernameRequest {
  desiredUsername: string;
  credentialId: string;
}

export interface SwitchToStandardUsernameRequest {
  newStandardUsername?: string;
}

export interface RenewPremiumUsernameRequest {
  premiumUsernameId: string;
  paymentIntentId?: string;
}

export interface CreateRenewalPaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
}

const premiumUsernameService = {
  checkAvailability: async (username: string): Promise<PremiumUsernameAvailability> => {
    return apiService.get<PremiumUsernameAvailability>(`/api/PremiumUsername/check/${encodeURIComponent(username)}`);
  },

  getMyPremiumUsername: async (): Promise<PremiumUsernameDto | null> => {
    try {
      return await apiService.get<PremiumUsernameDto>('/api/PremiumUsername/my-premium-username');
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  claimPremiumUsername: async (request: ClaimPremiumUsernameRequest): Promise<PremiumUsernameResult> => {
    return apiService.post<PremiumUsernameResult>('/api/PremiumUsername/claim', request);
  },

  changeToPremiumUsername: async (newPremiumUsername: string): Promise<PremiumUsernameResult> => {
    return apiService.post<PremiumUsernameResult>('/api/PremiumUsername/change-to-premium', {
      newPremiumUsername
    });
  },

  switchToStandardUsername: async (premiumUsernameId: string): Promise<PremiumUsernameResult> => {
    return apiService.post<PremiumUsernameResult>('/api/PremiumUsername/switch-to-standard', { premiumUsernameId });
  },

  createRenewalPaymentIntent: async (): Promise<CreateRenewalPaymentIntentResponse> => {
    return apiService.post<CreateRenewalPaymentIntentResponse>('/api/PremiumUsername/create-renewal-payment-intent');
  },

  renewPremiumUsername: async (premiumUsernameId: string, paymentIntentId?: string): Promise<PremiumUsernameResult> => {
    return apiService.post<PremiumUsernameResult>('/api/PremiumUsername/renew', {
      premiumUsernameId,
      paymentIntentId
    });
  },

  isPremiumFormat: async (username: string): Promise<boolean> => {
    return apiService.get<boolean>(`/api/PremiumUsername/is-premium/${encodeURIComponent(username)}`);
  },

  isStandardFormat: async (username: string): Promise<boolean> => {
    return apiService.get<boolean>(`/api/PremiumUsername/is-standard/${encodeURIComponent(username)}`);
  },
};

export default premiumUsernameService;
